import { useLocation, useNavigate } from 'react-router-dom'

import { FlagKuwait } from '@/app/components/flags/FlagKuwait'
import { FlagUk } from '@/app/components/flags/FlagUk'
import { swapLocaleInPathname } from '@/content/i18n'
import { cn } from '@/lib/utils'
import { useUiStore } from '@/store/useUiStore'
import type { Direction, Locale } from '@/types/site'

type LocaleSwitcherProps = {
  locale: Locale
  /** Matches page `dir` (Arabic → rtl) */
  direction?: Direction
  /** Hero / dark video background */
  variant?: 'default' | 'light'
  /** Icon-only: flag in a compact control (e.g. dashboard header). */
  mode?: 'default' | 'icon'
  /** Extra classes (e.g. compact dashboard header: `h-10`) */
  className?: string
}

export function LocaleSwitcher({
  locale,
  direction = 'ltr',
  variant = 'default',
  mode = 'default',
  className,
}: LocaleSwitcherProps) {
  const location = useLocation()
  const navigate = useNavigate()
  const setLocalePreference = useUiStore((state) => state.setLocalePreference)

  const nextLocale: Locale = locale === 'en' ? 'ar' : 'en'

  function handleSwitch() {
    setLocalePreference(nextLocale)
    navigate(`${swapLocaleInPathname(location.pathname, nextLocale)}${location.search}${location.hash}`)
  }

  const isLight = variant === 'light'
  const isIcon = mode === 'icon'
  /** Flag for the **current** UI language (KW = Arabic, UK = English). */
  const CurrentFlag = locale === 'ar' ? FlagKuwait : FlagUk
  const label = locale === 'en' ? 'English' : 'العربية'
  const aria =
    locale === 'en' ? 'Switch language to Arabic' : 'التبديل إلى الإنجليزية'

  return (
    <button
      type="button"
      dir={direction}
      onClick={handleSwitch}
      title={locale === 'en' ? 'العربية' : 'English'}
      aria-label={aria}
      className={cn(
        'inline-flex shrink-0 items-center justify-center border transition',
        isIcon
          ? 'h-10 w-10 rounded-full border-slate-200/90 bg-white text-slate-800 shadow-sm hover:border-slate-300 hover:bg-slate-50'
          : 'h-12 gap-2 rounded-xl border px-5 text-base font-bold leading-none lg:text-lg',
        !isIcon &&
          (isLight
            ? 'border-white/60 bg-transparent text-white hover:border-white hover:bg-white/10'
            : 'border-slate-300 bg-white text-slate-800 hover:border-slate-400 hover:bg-slate-50'),
        className,
      )}
    >
      <span
        className={cn(
          'relative overflow-hidden rounded-sm shadow-inner ring-1 ring-black/5',
          isIcon ? 'h-6 w-6 rounded-full' : 'h-5 w-8',
        )}
      >
        <CurrentFlag className="h-full w-full object-cover" />
      </span>
      {!isIcon ? <span className="whitespace-nowrap">{label}</span> : null}
    </button>
  )
}
